/**
 * Wire shapes of the Bands Studio API (`/band-studio/*`), mirroring
 * CONTRACT.md 2026-10-10. Snake_case on purpose: the API owns these names and
 * the pages bind to them directly.
 */

/** Partition columns to the values a model covers; `null` = the whole tenant. */
export type BandScope = Record<string, string[]> | null;

export type RunStatus = 'queued' | 'running' | 'succeeded' | 'failed';

/** What a model is now: one thing with a state. A failed model is edited in place; a complete one only clones. */
export type ModelState = 'draft' | 'running' | 'failed' | 'complete';
/** What the page offers for a model, decided by the API from its state (api#1026). */
export type ModelAction = 'run' | 'edit' | 'clone';
export type RunExecutor = 'local' | 'legacy';

export interface RunProgress {
  step: string;
  done: number;
  total: number;
}

export interface RunParity {
  passed: boolean;
  label: string;
}

export interface RunRow {
  run_id: string;
  short_id: string;
  slug: string;
  /** Internal: which saved logic ran. Never shown; every run is a model of its own (10/21). */
  version: number | null;
  /** The group the model is tracked under (a label only); null when it is in none. */
  group?: string | null;
  /** What the model covers in words ("US, Surgical"). */
  covers_label?: string;
  /** True for a run of the model's unsaved draft (its version is null). */
  draft: boolean;
  status: RunStatus;
  executor: RunExecutor;
  model_name: string;
  /** The name its maker typed, shown in place of the run name; null when they typed the run name. */
  display_name?: string | null;
  as_of_dtme: string;
  started_at: string | null;
  finished_at: string | null;
  seconds: number | null;
  created_by: string;
  what_changed: string | null;
  bands: number | null;
  moved: number | null;
  /** The run Moved is measured against: the previous completed run of this model. */
  moved_vs: MovedVs | null;
  parity: RunParity | null;
  has_trace: boolean;
  has_outputs: boolean;
  error: string | null;
  /** A failed run's reason on one line; the full traceback stays in `error`. */
  error_line: string | null;
  /** Where a failed run stopped and what to do, in the page's words; older API builds omit it. */
  error_reason?: string | null;
  failed_step: string | null;
  progress: RunProgress | null;
  /**
   * False when the API cannot start a draft from this run's logic. Older API
   * builds omit it, and those runs clone, so a missing field means clonable.
   */
  clonable?: boolean;
  /** The model's state; older API builds omit it and the page reads `status` instead. */
  state?: ModelState;
  /** The buttons the API allows for this state; older API builds omit it and the page derives them. */
  actions?: ModelAction[];
}

export interface MovedVs {
  run_id: string;
  short_id: string;
  version: number | null;
  /** "<model_name> · <as_of>" of that run. */
  label: string;
}

/** What the Bands and Moved columns count, from the API (column help). */
export interface RunColumns {
  bands: string;
  moved: string;
}

export type VersionStatus = 'draft' | 'current' | 'archived';

export interface VersionRow {
  version: number;
  status: VersionStatus;
  notes: string | null;
  created_by: string;
  created_at: string;
  legacy_model_name: string | null;
  changes_vs_previous: string | null;
}

export interface ModelCard {
  slug: string;
  title: string;
  scope: BandScope;
  scope_label: string;
  /** What the newest model covers in words, values only ("US, Surgical"). */
  covers_label?: string;
  /**
   * The group this card is (Michael, 10/21: cards are groups of models; a label only); null is
   * the one card of the models in no group (Michael, 10/28).
   */
  group?: string | null;
  /** Every lineage a model or draft of this group belongs to (internal). */
  slugs?: string[];
  template: string | null;
  origin_label: string;
  current_version: number | null;
  has_draft: boolean;
  /** The open draft: the version it started from, who opened it and when it last changed. */
  draft?: GroupDraft | null;
  /** Every open draft tracked in this group. */
  drafts?: GroupDraft[];
  versions: VersionRow[];
  runs: RunRow[];
}

/** An open draft: the lineage it lives on, its model name, who opened it and when it last changed. */
/** The failed attempt a draft tries again: to whoever edits it, the model is still that failure. */
export interface FailedRun {
  run_id: string;
  error_line: string | null;
  started_at: string | null;
}

export interface GroupDraft {
  slug?: string;
  base_version: number | null;
  /** The model name the draft's run will take. */
  model_name?: string | null;
  /** The name its maker typed, shown in place of the run name. */
  display_name?: string | null;
  covers_label?: string;
  created_by: string | null;
  updated_at: string | null;
  /** The failed attempt this draft fixes; null for a model that never ran. */
  failed_run?: FailedRun | null;
  /** What the row allows (api#1026); a draft that fixes a failed model also clones it. */
  actions?: ModelAction[];
}

export interface UncoveredScope {
  column: string;
  values: string[];
  note: string;
}

export interface ModelsResponse {
  tenant: string;
  models: ModelCard[];
  /** Every group name, for the group pickers. */
  groups?: string[];
  uncovered_scopes: UncoveredScope[];
  counts_label: string;
  run_columns: RunColumns;
}

export type SectionKey = 'data' | 'starting_bands' | 'rules' | 'outputs';
export type CardBadge = 'house' | 'yours';
export type CardState = 'active' | 'off' | 'needs_decision';
export type ValueType = 'number' | 'percent' | 'text' | 'bool' | 'json' | 'choice';

/** The input a value edits with; the API picks it from the value's shape. */
export type ControlKind =
  | 'percent'
  | 'number'
  | 'toggle'
  | 'choice'
  | 'list'
  | 'columns'
  /** One data column name, picked from the input columns. */
  | 'column'
  | 'json'
  | 'text'
  | 'skipped'
  | 'percentiles'
  | 'chain'
  /** Columns and the values each must hold (a model's scope). */
  | 'filter'
  /** Price+'s subset run: a filter plus whether the previous model's bands stay for the other rows. */
  | 'subset'
  /** B-SKU or C-SKU levels: level name -> its columns, each picked from `ModelSummary.level_columns`. */
  | 'levels';

export interface ValueControl {
  kind: ControlKind;
  /** Stepper increment, in the raw unit (a percent control stores a fraction). */
  step?: number;
  min?: number | null;
  max?: number | null;
  choices?: string[];
  /** The house words for each choice code (pct_of_floor reads "% of floor"); the code shows when absent. */
  labels?: Record<string, string>;
  /** What a percent is of ("floor", "list"); shown after the number. */
  unit?: string;
  /** Columns control: what each chosen column holds on the transactions. */
  hints?: ColumnHint[];
  /** Chain control: the order of the chips matters. */
  ordered?: boolean;
  /** Number control: the value that means "no limit"; the box shows empty for it and an emptied box writes it. */
  open_value?: number;
  placeholder?: string;
  /** Levels control: the key inside the value that holds the levels (C-SKU keeps them under CSKU_LEVELS). */
  path?: string;
}

export interface ColumnHint {
  column: string;
  present: boolean;
  distinct?: number;
  /** "5 values · Zone 1, Zone 2…" or "not on the transactions". */
  text: string;
}

/** One gate of a hand-written waterfall: its words and how many passed it in the last run. */
export interface WaterfallStage {
  label: string;
  count: number;
}

/**
 * A setting edited inside a step's readout, as the key the page saves it under. The shared chat
 * carries it from a readout in a bubble to the page, so it lives with the wire shapes.
 */
export interface OutlineValueChange {
  key: string;
  value: unknown;
}

/** One node of a step's readout; the page draws the binding it names. */
export interface OutlineNode {
  /** `table`, `waterfall` and `example` are written by hand (a small table, gates with counts, a worked item). */
  type: 'group' | 'bullet' | 'signal' | 'table' | 'waterfall' | 'example';
  text: string;
  /**
   * A bullet's text carries each setting it uses as `{binding id}`, drawn in place. `binding` is the one
   * table or funnel drawn under the bullet, or null; a hand-written node is sent without it.
   */
  binding?: string | null;
  show: boolean;
  /** Every setting in the sentence holds the template value: it reads in place with an "unchanged" badge. */
  unchanged?: boolean;
  children: OutlineNode[];
  /** table: the column headings and the rows. */
  columns?: string[] | null;
  rows?: string[][] | null;
  /** waterfall: the gates in order, with counts. */
  stages?: WaterfallStage[] | null;
}

export type BindingKind = 'lever' | 'table' | 'chain' | 'count' | 'funnel' | 'outcome';

export type LeverControl =
  | 'percent'
  | 'integer'
  | 'months'
  | 'ratio'
  | 'number'
  | 'money'
  | 'switch'
  | 'choice'
  | 'skipped'
  | 'percentiles'
  | 'list'
  | 'text'
  | 'json';

export interface BindingCount {
  label: string;
  rows: number;
}

/** What a step's readout can draw, extracted by the API from the step's contract and last run. */
export interface ReadoutBinding {
  id: string;
  kind: BindingKind;
  label: string;
  key: string | null;
  value: unknown;
  /** lever: the chip and editor kind it reads in. */
  control: LeverControl | null;
  /** lever: a list of size-tier codes as the tier names its chip prints. */
  names?: string[] | null;
  ordered: boolean;
  /** False for a dict or column lever, which sits under "all settings", never in the outline. */
  show: boolean;
  /** lever and chain: the name stem whose levers read as one unit. */
  family: string | null;
  /** lever and chain: holds the model's template value and the draft has not changed it. */
  default: boolean;
  row_count: number | null;
  frame: string | null;
  counts: BindingCount[] | null;
  /** funnel: the frame's rows first, then the rows still passing after each gate. */
  steps: BindingCount[] | null;
  tiles: { value: number; label: string }[] | null;
  /** lever and chain: the setting the rest of the page edits through. */
  setting?: FacetSetting;
}

/** One bold label → value line at the top of a facet. */
export interface FacetFact {
  label: string;
  value: string;
}

export interface CardValue {
  key: string;
  label: string;
  display: string;
  raw: unknown;
  type: ValueType;
  choices: string[] | null;
  editable: boolean;
  reason: string | null;
  reason_author: string | null;
  control: ValueControl;
}

export interface ParamTable {
  key: string;
  label: string;
  columns: string[];
  rows: Record<string, unknown>[];
  row_count: number;
  truncated: boolean;
  /** Columns a row is matched on (the rest are values); the filter box searches these. */
  key_columns: string[];
  /** Columns a draft may add as keys: the frame the table joins to, less the columns it has. */
  legal_keys?: string[];
  /** One line on where the entries apply, in the tenant's words. */
  applies?: string;
  editable: boolean;
  reason: string | null;
}

/** One page of a param table, filtered on its key columns (`GET …/tables/{key}`). */
export interface TablePage {
  key: string;
  columns: string[];
  key_columns: string[];
  rows: Record<string, unknown>[];
  row_count: number;
  /** Rows the filter kept, across all pages. */
  matched: number;
}

export interface SummaryCard {
  id: string;
  section: SectionKey;
  title: string;
  badge: CardBadge;
  summary: string;
  description: string;
  state: CardState;
  values: CardValue[];
  tables: ParamTable[];
  groups: string[] | null;
  /** One sentence on what kind of thing this card is (replaces the engine fold). */
  about: string;
  /** Engine steps behind the card; empty for a pure setting, which has nothing to switch. */
  step_ids: string[];
  /** True for a step written for this tenant (its logic can change through the chat). */
  is_custom: boolean;
  changed_in_draft: boolean;
  /**
   * Custom output cards only: the id of the nearest earlier custom output step whose file
   * this one reads (a loading file's checks sit under it), or null for a file of its own.
   */
  parent?: string | null;
}

// ---- sections: the steps of a model as the rail and its sheet read them ----

/** One key setting a facet shows by default, with the control it edits with. */
export interface FacetSetting {
  key: string;
  label: string;
  display: string;
  raw: unknown;
  control: ValueControl;
  editable: boolean;
  reason: string | null;
  reason_author: string | null;
  card_id: string;
  card_title: string;
  /** Draft ETL date rows only: the file's own archive holds a newer date (GF-6415). */
  notice?: string | null;
  /** Why the setting is stored but unused, e.g. B-SKU levels while B-SKU bands is off (GF-6379). */
  off_note?: string;
}

/** One guardrail: a sentence, its state, and the one number that tunes it. */
export interface GuardrailRow {
  card_id: string;
  title: string;
  sentence: string;
  state: CardState;
  badge: CardBadge;
  groups: string[] | null;
  changed_in_draft: boolean;
  /** What this draft changed on the rule, one line each, without the rule's name. */
  changed_lines?: string[];
  setting: FacetSetting | null;
  /** A custom rule's own scalar settings. */
  levers: FacetSetting[];
  can_toggle: boolean;
  /** How the engine applies the rule, in short sentences under its guarantee. */
  how?: string[];
  /** True for a rule at the house value (a $0 lower bound, 2% laddering): table stakes the page folds into one line. */
  house_default?: boolean;
}

export interface Facet {
  key: string;
  title: string;
  subtitle: string;
  /** The percentile fit only: the step of yours that sets every band in its place (10/21). */
  replaced_by?: { key: string; title: string } | null;
  facts: FacetFact[];
  prose: string;
  /** One line per step, shown as bullets instead of the prose when present. */
  points?: string[];
  settings: FacetSetting[];
  rules: GuardrailRow[];
  /** The cards folded into this facet, for "all settings". */
  card_ids: string[];
  /** Cards the section shows whole above All settings (the size-tier breaks); absent on older APIs. */
  in_view?: string[];
  changed_in_draft: boolean;
  needs_decision: number;
  off: number;
  /** Story sections only: their place in the run, and whether the piece is the tenant's own. */
  number?: number;
  kind?: 'custom' | 'standard';
  state?: CardState;
  last_run?: string | null;
  can_toggle?: boolean;
  /** One line under the tile's title on the run strip. */
  blurb?: string;
  /** A custom step's readout: one-line verdict, grouped bullets, the bindings they draw. */
  verdict?: string;
  outline?: OutlineNode[];
  bindings?: Record<string, ReadoutBinding>;
  /** One sentence ahead of the outline: what the step decides and for whom. */
  gist?: string;
  /**
   * "fallback" while the written readout is pending (a quiet hint); "failed" when the author broke the
   * writing rules (the page says so, with `authored_error`); "standard" for the engine's own steps.
   */
  authored?: ReadoutAuthored | 'standard';
  authored_error?: string | null;
  /** The group this step reads in (one rail card with its steps inside); locked = the rail cannot drag it apart. */
  group?: FacetGroup | null;
  /** "What it writes" only: each output step of the tenant's own, read the same way as a custom step. */
  readouts?: FacetReadout[];
  /** Draft only: the change lines that belong to this section. */
  changed_lines?: string[];
  /** Where the section sits in the run: its stage and the places (from 1) its steps take. */
  place?: StoryPlace | null;
  /** Draft only: whether the rail can drag this section, and why not when it cannot. */
  move?: StoryMove | null;
}

/**
 * How a step's readout was written: by the model, by hand, or the plain view while the written
 * one is pending (`fallback`) or after its author failed (`failed`).
 */
export type ReadoutAuthored = 'model' | 'hand' | 'fallback' | 'failed';

/** An output step's readout inside "What it writes": its title, then gist and grouped bullets. */
export interface FacetReadout {
  card_id: string;
  title: string;
  verdict: string;
  gist: string;
  outline: OutlineNode[];
  bindings: Record<string, ReadoutBinding>;
  authored: ReadoutAuthored;
  authored_error?: string | null;
  last_run?: string | null;
  /** The custom output card whose file this step reads, or null; see `SummaryCard.parent`. */
  parent?: string | null;
}

/** Steps that read as one card on the rail, in their own order. */
export interface FacetGroup {
  key: string;
  title: string;
  locked: boolean;
}

/** How the page reads a model: its step layout; the words ride on the column labels. */
export interface ModelPresentation {
  /** Order numbers on the rail and the sheets; off unless the model says so. */
  show_order: boolean;
  steps?: Record<string, { name?: string | null; group?: string | null; hidden?: boolean }>;
  groups?: { key: string; title: string; steps: string[]; locked: boolean }[];
}

export type RunStage = 'init' | 'seed' | 'modify' | 'outputs';

export interface StoryPlace {
  stage: RunStage;
  first: number;
  last: number;
}

export interface StoryMove {
  movable: boolean;
  step_id: string | null;
  reason: string | null;
}

/** Body of POST /models/{slug}/draft/steps/{step_id}/move. */
export interface MoveStepRequest {
  /** The step's new place in the stage, from 1, once it has moved. */
  position: number;
  /** Sent only when the drop lands in another part of the run, so the API can say why it refuses. */
  stage?: RunStage;
  reason?: string;
}

export interface SummarySection {
  key: SectionKey;
  title: string;
  subtitle: string;
  cards: SummaryCard[];
}

export type BannerKind = 'info' | 'warn' | 'placed' | 'refused';

export interface SummaryBanner {
  kind: BannerKind;
  text: string;
}

/** A version number, or the literal `draft` the routes and the API share. */
export type VersionRef = number | 'draft';

export interface ModelSummary {
  slug: string;
  title: string;
  /** The draft's group (a draft summary only), null when in none; a run's group rides on `run.group`. */
  group?: string | null;
  /** A draft's last write; edits send it back, so a stale tab's edit is refused. */
  revision?: string | null;
  /** The version shown; null for the summary of a run that ran a bundle file. */
  version: VersionRef | null;
  status: string;
  scope: BandScope;
  scope_label: string;
  /** What the model covers in words, values only ("US, Surgical"). */
  covers_label?: string;
  template: string | null;
  origin_label: string;
  subtitle: string;
  run: RunRow | null;
  /** Draft only: the draft's own runs, newest first. */
  runs?: RunRow[];
  editable: boolean;
  /** The model's state; a draft summary is 'draft', a run summary carries its run's state. */
  state?: ModelState;
  /** The buttons the header offers; older API builds omit it and the page derives them from `run`. */
  actions?: ModelAction[];
  sections: SummarySection[];
  facets: Facet[];
  /** The model in the order it runs: the page reads top to bottom. */
  story: Facet[];
  /** Columns on the transactions this version reads, for the column pickers. */
  input_columns?: string[];
  /**
   * Per column setting (its key without "algo:": PRICING_SEGMENT_CONFIG, BUSINESS_SEGMENT_CONFIG,
   * FAMILY_COLS, CUSTOMER_COLS), the columns its picker offers: those whose values fit the
   * setting, with any column the model already uses kept in. A setting absent here reads
   * from `input_columns`.
   */
  column_candidates?: Record<string, string[]>;
  /**
   * The columns a B-SKU or C-SKU level may group by: segments, the family and the product hierarchy.
   * Null when they could not be read; absent when no level setting is on the page.
   */
  level_columns?: string[] | null;
  /** House rule ref -> its guardrail card, for the rules this model already has; the add dialog edits these instead of adding them twice. */
  rules_in_model?: Record<string, { card_id: string; state: string }>;
  /** A step's readout is still being written; the page reads again shortly. */
  readouts_pending?: boolean;
  /** The model's own segment columns: what a rule may ladder along. */
  segment_columns?: string[];
  needs_decision: number;
  banner: SummaryBanner | null;
  /** Draft only: the ETL date rows' notices, repeated beside Run; absent on older APIs. */
  etl_notices?: string[];
  /** After an add: the card of the rule just placed, so the page can open it. */
  added_card_id?: string;
  /** Draft only: one line per change against the base version (the save note starts from these). */
  changes?: string[];
  /** Notes of the saved version (null on a draft until it is saved). */
  notes: string | null;
  /** Draft only: the version number "Save" will create. */
  next_version?: number;
  /** Draft only: the saved version the draft started from, if any. */
  base_version?: number | null;
  /** Draft only: the Price+ model name Save and run gives the run. */
  model_name?: string | null;
  /** Draft only: the name its maker typed, shown in place of the run name. */
  display_name?: string | null;
  /** Where the draft, or the draft a version was saved from, started. */
  origin?: DraftOrigin | null;
  /** Draft only: the failed attempt it fixes, so the page still calls the model failed. */
  failed_run?: FailedRun | null;
  /**
   * The band summary layout set on the model. Null or absent when none is set:
   * the engine's default layout reads as noise, so the page shows no panel.
   */
  summary_layout?: SummaryLayoutView | null;
  /**
   * The run the band summary preview reads; null when no run has bands on this
   * machine. A draft with no run of its own previews the run it started from.
   */
  preview_run_id?: string | null;
  /** Why the preview reads a run that is not the draft's own; null otherwise. */
  preview_note?: string | null;
  presentation?: ModelPresentation | null;
  /**
   * Draft only: the custom steps removed from this draft, per stage. A removed step is
   * off, out of the rail and the cards, and comes back through the bank (`removed:<step_id>`).
   */
  removed?: RemovedSteps;
}

/** The stages the API files a removed step under; the rail folds data and seed into one. */
export type RemovedStage = 'data' | 'seed' | 'rules' | 'outputs';

/** One custom step removed from the draft, with one line of what it did. */
export interface RemovedStep {
  step_id: string;
  title: string;
  sentence: string;
}

export type RemovedSteps = Record<RemovedStage, RemovedStep[]>;

// ---- the step bank (GET …/draft/bank, POST …/draft/steps/add) ----

/** The stages a draft can take a step into from the bank: every part of the run. */
export type BankStageKey = 'data' | 'seed' | 'rules' | 'outputs';

/** Where a bank item comes from: the house catalogue, a saved version, or this draft's own removed rows. */
export type BankSource = 'house' | 'version' | 'removed';

/** One step the draft can take in; `ref` is what the add call takes back. */
export interface BankItem {
  ref: string;
  title: string;
  sentence: string;
  source: BankSource;
  step_id: string | null;
  /** version source: the saved version and model the copy comes from. */
  version: number | null;
  model: string | null;
  /** Always null today: binding a run line to a bank item is not cheap on the API. */
  last_run: string | null;
}

export interface BankStage {
  stage: BankStageKey;
  house: BankItem[];
  yours: BankItem[];
}

export interface BankResponse {
  stages: BankStage[];
}

/** Body of POST …/draft/steps/add. */
export interface AddKnownStepRequest {
  ref: string;
  /** The step (id or title) the new one follows; the API places it by its own rules otherwise. */
  after?: string;
  reason?: string;
}

/** One section of the band summary layout, as the panel lists it. */
export interface SummaryLayoutItem {
  kind: string;
  /** The kind in plain words (Headline, List, Table, ...). */
  kind_label: string;
  /** The section's title, or what it shows in plain words. */
  title: string;
}

/** The band summary layout of a version or draft (spec v2, Option D). */
export interface SummaryLayoutView {
  source: 'authored' | 'default';
  /** "Authored layout" or "Default layout". */
  label: string;
  sections: Record<string, unknown>[];
  items: SummaryLayoutItem[];
}

/** The layout resolved against one band of a run: the blocks the shared renderer draws. */
export interface SummaryPreviewResponse {
  run: RunRow;
  /** The band in words: its family and segment values. */
  band: string;
  layout: { source: 'authored' | 'default'; label: string };
  /** Resolved blocks (spec v2 §3); the renderer validates them. */
  blocks: unknown;
}

export interface DraftOrigin {
  kind: DraftStartKind;
  slug: string | null;
  version: number | null;
  run_id: string | null;
}

// ---- product search (summary-layout preview picker) ----

export interface ProductsResponse {
  keys: string[];
  /** Column holding the readable product name; not part of the band keys. */
  label_column: string | null;
  rows: Record<string, string>[];
}

export type ChatMode = 'read' | 'build';

/** The extra `context` object the Bands Studio chat sends with every turn. */
export interface BandChatContext {
  slug: string;
  /** The model the page shows: its run id, or `draft` on the draft page (api#1026). */
  run_id?: string | 'draft';
  compare?: { a: string; b: string };
  mode: ChatMode;
}

// ---- P3 · diff ----

export type CardChange = 'added' | 'removed' | 'changed' | 'moved';

export interface DiffSide {
  slug: string;
  title: string;
  version: number | null;
  run: RunRow | null;
  label: string;
}

export interface ValueChange {
  key: string;
  label: string;
  a: string;
  b: string;
  reason: string | null;
}

export interface CardDiff {
  card_id: string;
  title: string;
  badge: CardBadge;
  change: CardChange;
  value_changes: ValueChange[];
  position: { a: number | null; b: number | null } | null;
  summary: string;
}

export interface DiffSection {
  key: SectionKey;
  title: string;
  changed: number;
  unchanged_titles: string[];
  cards: CardDiff[];
}

export interface OutcomeTile {
  label: string;
  value: string;
}

export interface LargestMove {
  label: string;
  cell: string;
  /** The price that moved most: floor, target or stretch. */
  price?: 'floor' | 'target' | 'stretch';
  a: number;
  b: number;
  delta_pct: number;
  rule: string | null;
}

export interface Outcome {
  keys: string[];
  rows_a: number;
  rows_b: number;
  rows_both: number;
  moved: number;
  mean_target_change_pct: number | null;
  band_method_changed: number;
  tiles: OutcomeTile[];
  largest_moves: LargestMove[];
  attribution: { title: string; moved: number }[];
}

export interface DiffResponse {
  a: DiffSide;
  b: DiffSide;
  same_model: boolean;
  header: string;
  sections: DiffSection[];
  outcome: Outcome | null;
  outcome_note: string | null;
}

// ---- P4 · create ----

export interface TemplateRow {
  key: string;
  title: string;
  description: string;
  price_variable: string;
}

/** The three ways a laddered rule can close a gap between tiers; the API adds one step per direction. */
export type RuleDirection = 'center' | 'raise' | 'lower';

export interface HouseRule {
  ref: string;
  title: string;
  description: string;
  section: SectionKey;
  /** The one row the rule's param file starts with: its amount (`*_param`) and type (`*_type`) columns, the Price+ setup defaults. */
  starter?: Record<string, unknown>;
  /** [the starter row in customer words, the thing the user sets]: "5% of the floor", "width". */
  starter_words?: [string, string];
  /** True when the rule runs once per direction. */
  directions?: boolean;
  /** Settings the rule needs before it is added (Other laddering: the segment it ladders along). */
  asks?: { key: string; label: string; choices: 'segment_columns' }[];
  /** For a rule whose values go row by row into its table: where they go, said in the dialog instead of a value. */
  after_add?: string;
  /** Per starter type column, its choices in the words the guardrail row uses ("% of the price"). */
  type_choices?: Record<string, { value: string; label: string }[]>;
}

export type DraftStartKind = 'version' | 'run' | 'template' | 'blank';

export interface DraftStart {
  kind: DraftStartKind;
  slug?: string;
  version?: number;
  run_id?: string;
  template?: string;
}

export interface CreateDraftRequest {
  start: DraftStart;
  /** model_name: the new Price+ model name the user chose; every draft is a new model. */
  model: {
    slug: string;
    title?: string;
    scope?: BandScope;
    model_name: string;
    /** The name typed when it differs from model_name; null clears it, omitted keeps it. */
    display_name?: string | null;
    /** The group the new model is tracked under; the source's group when omitted. */
    group?: string | null;
  };
}

/** A suggested free model name, Price+'s rule for one, and why a typed name cannot be used. */
export interface ModelNameCheck {
  suggestion: string;
  pattern: string;
  max_length: number;
  /** Only when a name was checked: null when it is free. */
  problem?: string | null;
}

export interface CustomRule {
  title: string;
  description: string;
  code: string;
  params: Record<string, unknown>;
  groups?: string[];
  /** `last` pins the rule after every other; null lets the system place it. */
  position: 'last' | null;
}

/** A post-process step: a file the run writes once the bands are final (a loading file). */
export interface CustomOutputStep {
  title: string;
  description: string;
  code: string;
  params: Record<string, unknown>;
  /** The columns the file must hold; a run stops when it lacks one. */
  columns?: string[];
  /** The output step the new one follows (its id or title); the system puts it last otherwise. */
  after?: string | null;
}

export interface AddRuleRequest {
  house_rule?: string;
  groups?: string[];
  /** House rule only: the starter row's amount and type as the user set them, `{<param_col>: amount, <type_col>: type}`. */
  starter?: Record<string, unknown>;
  /** House rule with directions only: the directions to add; all three when absent. */
  directions?: RuleDirection[];
  /** House rule only: the settings it asks for, `{OTHER_LADDERING_COL: column}`. */
  settings?: Record<string, string>;
  custom?: CustomRule;
  output?: CustomOutputStep;
  reason?: string;
}

/** The file a post-process step writes, from a sample of one run: its columns and first rows. */
export interface OutputPreview {
  columns: string[];
  rows: Record<string, unknown>[];
  row_count: number;
  sample_rows: number;
  run_id: string;
  run_label: string;
  filename: string | null;
  /** One sentence on what the rows are a sample of. */
  note: string;
}

export interface PatchScopeRequest {
  scope: BandScope;
  title?: string;
  reason?: string;
}

/** GET …/draft/rules/{card}/removal: the steps that read what only this card makes leave with it. */
export interface RemovalPlan {
  card_id: string;
  title: string;
  also: { card_id: string; title: string }[];
}

/** One column the draft names that its run will not have (GET …/draft/audit). */
export interface AuditFinding {
  /** `block` stops Run; `warn` is said and the run goes on. */
  level: 'block' | 'warn';
  target: string;
  title: string;
  columns: string[];
  text: string;
  /** The set_value edits the chat applies to clear it; empty when a person must choose. */
  fixes: { key: string; value: unknown }[];
}

/** The pre-run column check: every column the draft names, against what the Data tab says the run will have. */
export interface DraftAudit {
  findings: AuditFinding[];
  blocking: number;
}

/**
 * A Run press, kept by the API while its checks, save and submit go on in the background: a large
 * model takes longer than the gateway allows one request. The page reads it until it ends.
 */
export interface RunDraftResponse {
  request_id: string;
  status: 'starting' | 'started' | 'refused' | 'failed';
  run_id: string | null;
  version: number | null;
  model_name: string | null;
  /** The name the model's maker typed, when one was typed. */
  display_name?: string | null;
  /** Why it did not start, in the API's words; null while starting or once started. */
  message: string | null;
}

export interface PropagateResponse {
  drafts: { slug: string; summary_line: string }[];
}

/** Slug rule for a new model: lowercase words joined by single hyphens. */
export const MODEL_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** A diff ref: `slug@17` for a saved logic, `slug@draft` for the open draft. */
export function versionRef(slug: string, version: VersionRef): string {
  return `${slug}@${version}`;
}

/** A diff ref for a model run: `run:<id>`. */
export function runRef(runId: string): string {
  return `run:${runId}`;
}

/** A column a covers or subset condition may name, with the tenant's word for it where one exists. */
export interface FilterColumn {
  name: string;
  label: string | null;
}

/** The values a condition on one column may pick, most common first, from the recipe's data. */
export interface ColumnValues {
  column: string;
  values: string[];
  total: number;
  truncated: boolean;
}

/** Which recipe's data a covers condition reads: a version, the draft, or (no slug) the tenant's newest ETL. */
export interface FilterSource {
  slug?: string | null;
  version?: number | 'draft' | null;
}
